import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Container, Typography, Card, CardContent, 
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, alpha, Grid
} from '@mui/material';
import { 
  Assessment, Security, DesignServices, ArrowForward, Search, 
  HelpOutline, AutoAwesome, Speed, CloudSync, ExpandMore, 
  BugReport, Architecture, Shield, Terminal 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Code Audits",
    question: "What does a technical codebase audit cover?",
    answer: "A complete audit evaluates structural architecture, memory management, anti-patterns, security vulnerabilities (OWASP top 10), technical debt, automated test coverage, and dependency health across React, React Native, Flutter, or Android codebases."
  },
  {
    category: "Code Audits",
    question: "How long does a comprehensive code audit take?",
    answer: "A standard codebase audit for a mid-sized application typically takes 3 to 5 business days. You receive an executive summary, a prioritized technical debt log, and a step-by-step remediation roadmap."
  },
  {
    category: "Cloud Architecture",
    question: "How do you optimize cloud backend costs (e.g., Firebase, AWS)?",
    answer: "Cost optimization involves restructuring database query patterns (denormalization vs indexing), setting up caching layers (Redis/CDN), removing unnecessary serverless execution triggers, and implementing automated resource scaling."
  },
  {
    category: "Cloud Architecture",
    question: "Should my startup use Firebase or custom microservices on AWS/GCP?",
    answer: "Firebase/Serverless is recommended for early-stage MVPs requiring rapid market validation and zero ops overhead. As transaction throughput scales or complex data querying needs arise, transitioning to containerized microservices (Docker/Kubernetes) on AWS or GCP becomes more cost-effective."
  },
  {
    category: "Tech Stack Strategy",
    question: "How do you evaluate cross-platform vs native development for a business?",
    answer: "Evaluation is based on product requirements, team expertise, time-to-market goals, hardware integration needs (Bluetooth, native sensors), and long-term maintenance budgets. We weigh trade-offs between Flutter, React Native, and Native Swift/Kotlin."
  },
  {
    category: "Tech Stack Strategy",
    question: "When is the right time to pay down technical debt versus building new features?",
    answer: "Technical debt should be addressed when feature velocity drops noticeably, crash rates rise above 0.1%, or regression bugs slow down release cycles. Refactoring is best integrated incrementally alongside sprint deliverables rather than delaying releases for full rewrites."
  },
  {
    category: "Security & CI/CD",
    question: "How do you secure cloud applications against credential leaks and data breaches?",
    answer: "Security measures include enforcing strict IAM role isolation, encrypting data at rest and in transit, implementing zero-trust secret management (AWS Secrets Manager/Vault), and setting up automated SAST/DAST pipeline scanning."
  },
  {
    category: "Security & CI/CD",
    question: "What CI/CD workflow best accelerates mobile and web release cadence?",
    answer: "Automated pipelines using GitHub Actions or Fastlane execute linting, unit tests, security scans, and automated builds upon pull requests, deploying directly to TestFlight, Google Play Internal Track, or Vercel staging environments."
  }
];

const ITConsulting = () => {
  const navigate = useNavigate();

  const siteUrl = "https://vikashtiwaridev.web.app/services/it-consulting";
  const pageTitle = "IT & Technical Architecture Consulting | Vikash Tiwari";
  const pageDescription = "Expert technical consulting: Codebase audits, cloud architecture optimization, tech stack selection, security hardening, and performance strategy.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "IT Architecture & Strategy Consulting",
    "serviceType": "IT Consulting",
    "provider": {
      "@type": "Person",
      "name": "Vikash Tiwari",
      "url": "https://vikashtiwaridev.web.app/"
    },
    "areaServed": "Worldwide",
    "description": pageDescription
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Code Audits', 'Cloud Architecture', 'Tech Stack Strategy', 'Security & CI/CD'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const services = [
    { 
      title: "Codebase & Architecture Audits", 
      desc: "Identify memory leaks, architectural anti-patterns, technical debt, and security flaws across existing mobile and web applications.",
      icon: <BugReport sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "Mobile & Cloud Tech Strategy", 
      desc: "Identify the optimal stack (Native, Cross-Platform, Web, or Serverless) aligned with your business model, budget, and growth roadmap.",
      icon: <Architecture sx={{ fontSize: 30, color: '#7C4DFF' }} />
    },
    { 
      title: "Backend & Cloud Infrastructure", 
      desc: "Design scalable cloud database structures, security rules, serverless functions, and cost-efficient API data processing pipelines.",
      icon: <CloudSync sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "Performance & CI/CD Pipelines", 
      desc: "Optimize application start-up latency, reduce bundle overhead, and implement automated GitHub Actions and Fastlane release workflows.",
      icon: <Speed sx={{ fontSize: 30, color: '#7C4DFF' }} />
    }
  ];

  const metrics = [
    { value: "40%+", label: "Avg. Cloud Cost Savings" },
    { value: "0", label: "Critical Vulnerabilities" },
    { value: "<100ms", label: "Target API Latency" },
    { value: "100%", label: "Audit Rule Coverage" }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        <meta name="keywords" content="IT Consultant, Code Audit Services, Mobile Architecture Consultant, Firebase Strategy, Software Advisor India" />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>
        
        {/* ================= HERO SECTION ================= */}
        <Box 
          sx={{ 
            position: 'relative', 
            pt: { xs: 10, md: 16 }, 
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0.08) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }} 
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>
              
              <Chip 
                icon={<AutoAwesome sx={{ fontSize: '18px !important', color: '#00E5FF' }} />} 
                label="Strategic Advisory" 
                sx={{ 
                  mb: 3, 
                  py: 2, 
                  px: 1,
                  bgcolor: alpha('#00E5FF', 0.08), 
                  color: '#00E5FF', 
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }} 
              />

              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' }, 
                  fontWeight: 900, 
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                IT & Software{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Architecture Consulting
                </Box>
              </Typography>

              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#94A3B8', 
                  fontSize: { xs: '1.1rem', md: '1.35rem' }, 
                  fontWeight: 400, 
                  lineHeight: 1.6, 
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                Make informed technology decisions, eliminate costly architecture mistakes, secure your infrastructure, and scale your digital systems efficiently.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button 
                  variant="contained" 
                  size="large" 
                  endIcon={<ArrowForward />} 
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    boxShadow: '0px 8px 24px rgba(0, 229, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #00D2EE 0%, #6B38FF 100%)',
                      boxShadow: '0px 12px 32px rgba(0, 229, 255, 0.45)',
                    }
                  }}
                >
                  Book Technical Consultation
                </Button>

                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#00E5FF',
                      bgcolor: alpha('#00E5FF', 0.05)
                    }
                  }}
                >
                  Technical FAQs
                </Button>
              </Stack>

              {/* Stats Bar */}
              <Grid container spacing={2}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: '16px', 
                        bgcolor: 'rgba(15, 23, 42, 0.6)', 
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 800, 
                          background: 'linear-gradient(90deg, #00E5FF, #7C4DFF)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>

        {/* ================= CORE CONSULTING SERVICES (Strict 2x2 Grid) ================= */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' }, 
                fontWeight: 800, 
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Consulting & Architecture Domain
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Strategic guidance to prevent costly rewrites, secure software pipelines, and maximize platform execution speed.
            </Typography>
          </Box>

          {/* 2x2 Grid with Separated Container Padding */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              rowGap: { xs: 3, md: 4 },
              columnGap: { xs: 3, md: 4 }
            }}
          >
            {services.map((item, i) => (
              <Card 
                key={i}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  borderRadius: '20px', 
                  bgcolor: 'rgba(15, 23, 42, 0.5)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 3,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 229, 255, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box 
                    sx={{ 
                      width: 56, height: 56, borderRadius: '14px', 
                      bgcolor: 'rgba(255, 255, 255, 0.03)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= TECH STACK & AUDIT TOOLCHAIN ================= */}
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, 
            bgcolor: 'rgba(15, 23, 42, 0.7)', 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                Audit & Infrastructure Toolchain
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                Industry-standard tooling utilized for deep static analysis, profiling, and cloud optimization.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1.5 }}>
              {[
                "Firebase Strategy", "AWS / GCP Architecture", "SonarQube", "OWASP ZAP", 
                "GitHub Actions", "Fastlane", "Docker", "Sentry", 
                "Datadog", "Terraform", "React Native / Flutter Audits", "Postgres / MongoDB Optimization"
              ].map((tech) => (
                <Chip 
                  key={tech} 
                  label={tech} 
                  sx={{ 
                    py: 2.5, px: 2, 
                    fontSize: '0.95rem', 
                    fontWeight: 600,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    color: '#E2E8F0',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#00E5FF', 0.1),
                      borderColor: '#00E5FF',
                      color: '#00E5FF'
                    }
                  }} 
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ================= TECHNICAL FAQ SECTION ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#7C4DFF' }} />} 
              label="Consulting FAQs" 
              sx={{ mb: 2, bgcolor: alpha('#7C4DFF', 0.1), color: '#7C4DFF', fontWeight: 600 }} 
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Detailed insights on code audit timelines, cloud cost containment, security standards, and stack selection.
            </Typography>
          </Box>

          {/* Search Bar & Category Filter */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search Consulting FAQs (e.g., Code audit, Firebase cost, CI/CD, security)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00E5FF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#00E5FF' },
                  '&.Mui-focused fieldset': { borderColor: '#7C4DFF' }
                }
              }}
            />

            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#00E5FF', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': { 
                  color: '#94A3B8', 
                  fontWeight: 600, 
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#00E5FF' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#00E5FF' }} />}
                      sx={{ p: 2.5, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip 
                        label={faq.category} 
                        size="small" 
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try searching for alternative topics or select "All".</Typography>
              </Box>
            )}
          </Box>

        </Container>

        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15) 0%, rgba(124, 77, 255, 0.15) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.25)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Need an Independent Architectural Evaluation?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Schedule a 1-on-1 technical consultation to audit your stack, reduce infrastructure costs, and eliminate technical debt before it impacts your growth.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Book Architecture Audit
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default ITConsulting;